/**
 * PORTED — RO-3-00g / #44474.
 * Origin: `excellent/app/work/work-surface.tsx` (verbatim, minus the inert
 * `"use client"`).
 *
 * Changes:
 *   `useRouter`/`usePathname`/`useSearchParams` from next/navigation
 *     → `useNavigate`/`usePathname`/`useSearch` from @excellent/ui/router
 *   `markInboxRead`/`markAllInboxRead` from work actions module
 *     → `apiCall(workInbox.read|readAll)` (RO-2-09)
 *
 * `@/work/rows` and `@/work/types` stay via the `@/` alias — pure modules, no
 * server sink (same pattern as mode-play → @/modes/play-route).
 */

import { useState, useTransition } from "react";
import { useNavigate, usePathname, useSearch } from "@excellent/ui/router";
import { apiCall } from "@excellent/ui/api";
import { workInbox } from "@excellent/ui/api/contracts/work-inbox";
import {
  Card,
  CardContent,
  Button,
  cn,
  INTENT_CLASSES,
  WorkList,
  type WorkRowModel,
  type Intent,
} from "@excellent/ui";
import { Check, X } from "@excellent/ui/icons";
import { workItemToRow } from "@/work/rows";
import type { WorkItem, WorkKind } from "@/work/types";
import { decideTaskReview, promptDecisionReason } from "./review-decision";

/**
 * The Work Inbox surface (docs/inbox/PRD.md §4) — rendered through the shared
 * `WorkList` (`@excellent/ui`) via the ONE `workItemToRow` mapping (also used by
 * the Issues unified list, so the row is identical everywhere). ONE Card whose
 * flush caption is the segmented KIND filter (dot · label · count), capping the
 * flat work-row list. Opening a row marks it read (inbox_state, v78) and opens
 * the task peek in place.
 */

// Segment labels/intents for the kind filter (the surfaces this inbox slices by).
const SEGMENT_META: Record<"task" | "review" | "mention", { plural: string; intent: Intent }> = {
  task: { plural: "Tasks", intent: "info" },
  review: { plural: "Reviews", intent: "warning" },
  mention: { plural: "Mentions", intent: "info" },
};

// The inbox is actionable items only — open tasks, reviews awaiting you, and
// mentions. Done work + generative suggestions are excluded (the right rail owns
// awareness). Same membership the prior Inbox lens enforced.
const isActionable = (i: WorkItem) => i.status !== "done" && i.kind !== "suggestion";

// ── Segmented kind filter (the queue's status-filter bar, by kind) ────────────
type Segment = "all" | keyof typeof SEGMENT_META;
const SEGMENTS: Segment[] = ["all", "task", "review", "mention"];

function segmentMeta(key: Segment): { label: string; intent: Intent } {
  if (key === "all") return { label: "All", intent: "neutral" };
  const m = SEGMENT_META[key];
  return { label: m.plural, intent: m.intent };
}

export function WorkSurface({ items }: { items: WorkItem[] }) {
  const router = useNavigate();
  const pathname = usePathname();
  const search = useSearch();
  const [segment, setSegment] = useState<Segment>("all");
  // Optimistic overlay on top of the server-persisted read watermark (inbox_state).
  const [optRead, setOptRead] = useState<Set<string>>(new Set());
  const [, start] = useTransition();
  const [msg, setMsg] = useState<string | null>(null);

  // A verify decision — same act-then-refresh shape as the unified Issues list.
  const act = (fn: () => Promise<unknown>) =>
    start(async () => {
      setMsg(null);
      try {
        await fn();
        router.refresh();
      } catch (e) {
        setMsg(`Failed: ${(e as Error)?.message ?? "unknown"}`);
      }
    });

  const onOpen = (item: WorkItem) => {
    setOptRead((s) => new Set(s).add(item.id));
    void apiCall(workInbox.read, { body: { itemKey: item.id } });
    // A task/review opens the shared task peek IN PLACE (`?task=<id>`) — the
    // same global TaskPeekHost the queue drives. Everything else navigates.
    if (item.taskId != null) {
      const next = new URLSearchParams(search?.toString());
      next.set("task", String(item.taskId));
      router.replace(`${pathname}?${next.toString()}`);
    } else if (item.href) {
      router.push(item.href);
    }
  };

  const isUnread = (i: WorkItem) => !i.read && !optRead.has(i.id);
  const actionable = items.filter(isActionable);
  const count = (seg: Segment) =>
    seg === "all" ? actionable.length : actionable.filter((i) => i.kind === seg).length;
  const rows = segment === "all" ? actionable : actionable.filter((i) => i.kind === segment);

  const unreadCount = rows.filter(isUnread).length;
  const markAllRead = () => {
    const ids = rows.map((i) => i.id);
    setOptRead((s) => {
      const n = new Set(s);
      ids.forEach((id) => n.add(id));
      return n;
    });
    void apiCall(workInbox.readAll, { body: { itemKeys: ids } });
  };

  // The shared work-row mapping (same one the Issues unified list uses). A
  // review row with scope gains the Accept/Reject verify decision through the
  // ONE shared handler module (`./review-decision` → override.human) — the same
  // path the unified Issues list uses, not a second one (P2-U1).
  const model: WorkRowModel[] = rows.map((i) => {
    const row = workItemToRow(i, onOpen);
    if (i.kind === "review" && i.taskId != null && i.projectSlug && i.sectionSlug) {
      const base = {
        taskId: i.taskId,
        projectSlug: i.projectSlug,
        sectionSlug: i.sectionSlug,
      };
      row.actions = [
        {
          icon: Check,
          label: "Accept",
          onClick: () => act(() => decideTaskReview({ ...base, decision: "accept" })),
        },
        {
          icon: X,
          label: "Reject",
          onClick: () => {
            const reason = promptDecisionReason("reject");
            if (reason === undefined) return;
            act(() => decideTaskReview({ ...base, decision: "reject", reason }));
          },
        },
        ...(row.actions ?? []),
      ];
    }
    return row;
  });

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="px-0">
        {/* Segmented kind filter — the queue's status-filter bar, reused. */}
        <header className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
          <div role="tablist" aria-label="Filter inbox by kind" className="-mx-1 flex items-center gap-1 overflow-x-auto px-1">
            {SEGMENTS.map((key) => {
              const on = segment === key;
              const meta = segmentMeta(key);
              return (
                <Button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  variant={on ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setSegment(key)}
                  className="shrink-0 gap-2"
                >
                  <span aria-hidden className={cn("size-2 rounded-full", INTENT_CLASSES[meta.intent].dot)} />
                  {meta.label}
                  <span className="type-subtext tabular-nums text-muted-foreground">{count(key)}</span>
                </Button>
              );
            })}
          </div>
          {unreadCount > 0 ? (
            <Button variant="ghost" size="sm" className="shrink-0" onClick={markAllRead}>
              Mark all read ({unreadCount})
            </Button>
          ) : null}
        </header>

        {msg ? <p className="px-4 pt-2 type-subtext text-muted-foreground">{msg}</p> : null}
        {/* Card-wrapped: inset rows to align with the padded kind-filter header. */}
        <WorkList rows={model} gutter empty="You're all clear — nothing needs you here." />
      </CardContent>
    </Card>
  );
}
